<template>
  <div>
    <div class="d-flex align-center justify-space-between ga-3 mb-4">
      <div
        class="d-flex align-center ga-2 text-uppercase font-weight-bold text-medium-emphasis font-size-11">
        <v-icon color="deep-purple" size="x-small">mdi-circle</v-icon>
        Оцінка ризику та можливі збитки
        <v-chip
          color="deep-purple-lighten-4"
          density="compact"
          rounded="pill"
          size="small">
          <span class="font-weight-bold text-deep-purple">
            {{ riskAssessmentCount }}
          </span>
        </v-chip>
      </div>

      <v-btn
        v-if="canCreateRiskAssessment"
        color="primary"
        density="compact"
        variant="text"
        @click="openRiskAssessmentDialog">
        <div class="d-flex align-center ga-1 font-size-12 text-blue-darken-1">
          <v-icon icon="mdi-plus"></v-icon>
          Додати оцінку
        </div>
      </v-btn>
    </div>

    <v-divider class="mb-4 border-opacity-10"></v-divider>

    <template v-if="riskAssessments.length">
      <v-card
        v-for="(assessment, index) in riskAssessments"
        :key="assessment.id || index"
        border
        class="font-size-13 mb-4">
        <v-card-text>
          <div class="d-flex align-center justify-space-between ga-3 mb-4">
            <div class="d-flex align-center ga-3">
              <v-avatar
                class="bg-deep-purple-lighten-5 text-deep-purple font-weight-bold"
                size="32">
                {{ index + 1 }}
              </v-avatar>
              <div class="font-size-13 font-weight-bold">
                Оцінка №{{ index + 1 }}
              </div>
              <v-chip
                v-if="assessment.probabilityLevel"
                :style="
                  getIncidentRiskProbabilityStyle(
                    assessment.probabilityLevelId ||
                      assessment.probabilityLevel?.id
                  )
                "
                class="font-weight-bold text-uppercase"
                density="compact"
                rounded="lg"
                size="small"
                variant="flat">
                {{ assessment.probabilityLevel.name }}
              </v-chip>
            </div>

            <v-btn
              v-if="canEditIncident"
              v-tooltip="'Видалити'"
              color="error"
              density="comfortable"
              icon="mdi-delete-outline"
              variant="text"
              @click="deleteRiskAssessment(assessment.id)"></v-btn>
          </div>

          <v-row>
            <v-col cols="12" md="6">
              <b-input-number
                :model-value="assessment.damageCost"
                hide-details
                label="Сума збитків, грн"
                rank-spacing
                readonly></b-input-number>
            </v-col>

            <v-col cols="12" md="6">
              <b-input
                :model-value="assessment.damageCostBased"
                hide-details
                label="Базовий показник"
                readonly></b-input>
            </v-col>

            <v-col cols="12" md="6">
              <b-input-number
                :model-value="assessment.damageCostPotential"
                hide-details
                label="Можливі втрати, грн"
                rank-spacing
                readonly></b-input-number>
            </v-col>

            <v-col cols="12" md="6">
              <b-input
                :model-value="assessment.probabilityLevel?.name"
                hide-details
                label="Ймовірність реалізації"
                readonly></b-input>
            </v-col>

            <v-col cols="12">
              <b-textarea
                :model-value="assessment.description"
                hide-details
                label="Опис оцінки"
                readonly
                rows="4"></b-textarea>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card border class="bg-deep-purple-lighten-5 mb-4">
        <v-card-text class="d-flex align-center ga-6 flex-wrap">
          <div class="d-flex align-center ga-3">
            <v-avatar color="deep-purple" rounded="lg" size="40">
              <v-icon color="white" icon="mdi-currency-usd"></v-icon>
            </v-avatar>
            <div>
              <div
                class="text-deep-purple text-uppercase font-weight-bold font-size-10">
                Загальна сума збитків
              </div>
              <div class="font-weight-bold font-size-15">
                {{ rankSpacing(totalDamageCost) }} грн
              </div>
            </div>
          </div>

          <v-divider
            class="border-deep-purple"
            length="40"
            vertical></v-divider>

          <div class="d-flex align-center ga-3">
            <v-avatar color="amber-darken-3" rounded="lg" size="40">
              <v-icon color="white" icon="mdi-alert-outline"></v-icon>
            </v-avatar>
            <div>
              <div
                class="text-amber-darken-4 text-uppercase font-weight-bold font-size-10">
                Загальні можливі втрати
              </div>
              <div class="font-weight-bold font-size-15">
                {{ rankSpacing(totalDamageCostPotential) }} грн
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </template>

    <div
      v-else
      class="d-flex align-center ga-2 text-medium-emphasis font-size-12 py-1">
      <v-icon icon="mdi-information-outline" size="small"></v-icon>
      Оцінку ризику не додано
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BInput, BInputNumber, BTextarea } from 'best-modules/components'
import { useIncident } from '../Incident'
import { getIncidentRiskProbabilityStyle } from '../../constants'
import { rankSpacing } from 'best-modules/utils'

defineOptions({
  name: 'RiskAssessmentBlock',
})

const {
  canCreateRiskAssessment,
  canEditIncident,
  deleteRiskAssessment,
  openRiskAssessmentDialog,
  riskAssessmentCount,
  riskAssessments,
  totalDamageCost,
  totalDamageCostPotential,
} = useIncident()
</script>
